import { useState, useRef } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Video, CheckCircle2, Clock } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const KINDS = [["sprint", "Sprint"], ["broad", "Broad Jump"], ["vertical", "Vertical"], ["general", "Other"]];

export default function AthleteVideos({ athleteId, videos, onChange }) {
  const [kind, setKind] = useState("sprint");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/videos?athlete_id=${athleteId}&kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Video uploaded — pending coach review.");
      onChange?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div data-testid="athlete-videos">
      <div className="bg-[#121212] border border-white/10 p-5 mb-4">
        <p className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 mb-3">Upload Film</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <select data-testid="video-kind" value={kind} onChange={(e) => setKind(e.target.value)} className="bg-[#0A0A0A] border border-white/15 text-sm px-3 py-2.5 outline-none focus:border-white">
            {KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input ref={fileRef} type="file" accept="video/*" onChange={upload} className="hidden" data-testid="video-file-input" id="video-upload-input" />
          <label htmlFor="video-upload-input" data-testid="video-upload-btn" className="inline-flex items-center gap-2 border border-white/20 px-5 py-2.5 text-xs uppercase tracking-widest font-bold hover:bg-white hover:text-black transition-colors cursor-pointer">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {uploading ? "Uploading..." : "Choose Video"}
          </label>
        </div>
      </div>

      {videos.length === 0 ? (
        <div className="text-white/40 text-sm border border-white/10 bg-[#121212] p-6" data-testid="videos-empty">No videos uploaded yet.</div>
      ) : (
        <div className="space-y-4" data-testid="videos-list">
          {videos.map((v) => (
            <div key={v.id} data-testid={`video-${v.id}`} className="bg-[#121212] border border-white/10 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2"><Video size={16} className="text-white/60" /><span className="font-bold uppercase tracking-tight">{v.kind}</span></div>
                <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 border ${v.status === "reviewed" ? "border-white bg-white/10" : "border-white/25 text-white/60"}`}>
                  {v.status === "reviewed" ? <CheckCircle2 size={11} /> : <Clock size={11} />} {v.status === "reviewed" ? "Reviewed" : "Pending"}
                </span>
              </div>
              <video controls src={`${BACKEND}/api/videos/${v.id}/file`} className="w-full max-h-72 bg-black border border-white/10" data-testid={`video-player-${v.id}`} />
              {v.status === "reviewed" && (
                <div className="mt-4 border-t border-white/10 pt-4" data-testid={`video-feedback-${v.id}`}>
                  {v.mechanics_score != null && <p className="font-mono-accent text-lg font-bold mb-2">Mechanics Score: {v.mechanics_score}/100</p>}
                  {v.coach_notes && <p className="text-white/70 text-sm mb-3">{v.coach_notes}</p>}
                  {Array.isArray(v.feedback) && v.feedback.length > 0 && (
                    <div className="space-y-1.5">{v.feedback.map((f, i) => (
                      <div key={i} className="flex gap-3 text-sm"><span className="font-mono-accent text-white/50 shrink-0">{f.timestamp}</span><span className="text-white/70">{f.note}</span></div>
                    ))}</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
